import React, { useState, useEffect } from "react";
import "./UserReports.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Avatar from "@mui/material/Avatar";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from "@mui/icons-material/Person";
import ReportIcon from "@mui/icons-material/Report";
import useMediaQuery from "@mui/material/useMediaQuery";
import moment from "moment";

function UserReports() {
  const uid = localStorage.getItem("id");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalReports, setTotalReports] = useState(0);
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const isMobile = useMediaQuery("(max-width:880px)");
  const isSmallMobile = useMediaQuery("(max-width:480px)");
  
  // View mode: 'grid' or 'list' - default to list
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    getReports();
  }, [page, searchTerm]);

  const getReports = () => {
    setLoading(true);
    AxiosUrl.post(`/fetch-user-reports/${uid}`, {
      page,
      limit,
      search: searchTerm,
    })
      .then((res) => {
        if (res.data.Error) {
          toast.error(res.data.Error);
          setLoading(false);
          return;
        }
        setReports(res.data.reports || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalReports(res.data.totalReports || 0);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to fetch user reports");
        setLoading(false);
      });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedReports(reports);
      setSelectAll(true);
    } else {
      setSelectedReports([]);
      setSelectAll(false);
    }
  };

  const handleSelectReport = (report) => {
    const isSelected = selectedReports.some(r => r._id === report._id);
    if (isSelected) {
      setSelectedReports(selectedReports.filter(r => r._id !== report._id));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedReports, report];
      setSelectedReports(newSelected);
      if (newSelected.length === reports.length) {
        setSelectAll(true);
      }
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (window.confirm("Are you sure you want to delete this user report?")) {
      try {
        const response = await AxiosUrl.post(`/delete-user-report/${uid}`, {
          reportId,
        });

        if (response.data.success) {
          toast.success(response.data.message);
          getReports();
        } else {
          toast.error(response.data.error || "Failed to delete user report");
        }
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Failed to delete user report");
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedReports.length === 0) {
      toast.warning("Please select reports to delete");
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${selectedReports.length} selected report(s)?`)) {
      try {
        const response = await AxiosUrl.post(`/bulk-delete-user-reports/${uid}`, {
          reports: selectedReports,
          deleteAll: false
        });

        if (response.data.success) {
          toast.success(response.data.message);
          setSelectedReports([]);
          setSelectAll(false);
          getReports();
        } else {
          toast.error(response.data.error || "Failed to delete reports");
        }
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Failed to delete reports");
      }
    }
  };

  const handleDeleteAll = async () => {
    if (window.confirm("Are you sure you want to delete ALL user reports? This action cannot be undone!")) {
      try {
        const response = await AxiosUrl.post(`/bulk-delete-user-reports/${uid}`, {
          deleteAll: true
        });

        if (response.data.success) {
          toast.success(response.data.message);
          setSelectedReports([]);
          setSelectAll(false);
          getReports();
        } else {
          toast.error(response.data.error || "Failed to delete all reports");
        }
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Failed to delete all reports");
      }
    }
  };

  const getProfilePicUrl = (profilePic) => {
    if (!profilePic) return null;
    return `https://backend.amarkhata.com/ProfilePic/${profilePic}`;
  };

  return (
    <div className="user-reports-container">
      <ToastContainer />
      <div className="user-reports-main">
        <div className="user-reports-content-wrapper">
          {selectedReports.length > 0 && (
            <div className="user-reports-bulk-actions">
              <span className="selected-count">{selectedReports.length} selected</span>
              <Button
                className="bulk-delete-button"
                onClick={handleDeleteSelected}
                variant="contained"
              >
                Delete Selected
              </Button>
              <Button
                className="delete-all-button"
                onClick={handleDeleteAll}
                variant="contained"
              >
                Delete All
              </Button>
            </div>
          )}

          <div className="user-reports-filter-section">
            <div>
              <label className="user-reports-filter-label" style={{ backgroundColor: "#e91e63" }}>
                Search
              </label>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search by username, mobile, or reason..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="user-reports-search-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>

          {/* View Toggle Buttons */}
          <div className="user-reports-view-toggle">
            <IconButton
              onClick={() => setViewMode('grid')}
              color={viewMode === 'grid' ? 'primary' : 'default'}
              title="Grid View"
              style={{
                backgroundColor: viewMode === 'grid' ? '#1976d2' : '#f5f5f5',
                color: viewMode === 'grid' ? 'white' : '#666',
                marginRight: '8px'
              }}
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
              title="List View"
              style={{
                backgroundColor: viewMode === 'list' ? '#1976d2' : '#f5f5f5',
                color: viewMode === 'list' ? 'white' : '#666'
              }}
            >
              <ViewListIcon />
            </IconButton>
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="user-reports-grid-container">
              {loading && <div className="google-progress-bar"></div>}
              {reports.length <= 0 && !loading ? (
                <div className="user-reports-no-data-available">
                  No user reports available
                </div>
              ) : (
                <div className="user-reports-grid">
                  {reports.map((report) => (
                    <Card key={report._id} className="user-reports-grid-card">
                      <div className="user-reports-grid-checkbox">
                        <Checkbox
                          checked={selectedReports.some(r => r._id === report._id)}
                          onChange={() => handleSelectReport(report)}
                          size="small"
                        />
                      </div>
                      <CardContent className="user-reports-grid-card-content">
                        <div className="user-reports-grid-header">
                          <div className="user-reports-grid-icon-wrapper">
                            <ReportIcon className="user-reports-grid-icon" />
                          </div>
                          <div className="user-reports-grid-header-info">
                            <Typography variant="h6" className="user-reports-grid-title">
                              User Report
                            </Typography>
                            {!isMobile && (
                              <Typography variant="caption" className="user-reports-grid-date">
                                {moment(report.Date).utcOffset('+06:00').format("DD MMM YYYY, hh:mm A")}
                              </Typography>
                            )}
                          </div>
                        </div>
                        
                        <div className="user-reports-grid-users">
                          <div className="user-reports-grid-user-section">
                            <div className="user-reports-grid-user-label">
                              <PersonIcon style={{ fontSize: 16, marginRight: 4 }} />
                              Reporter
                            </div>
                            <div className="user-reports-grid-user-info">
                              <Avatar
                                src={getProfilePicUrl(report.UserId?.ProfilePic)}
                                alt={report.UserId?.username}
                                className="user-reports-grid-avatar"
                              >
                                <AccountCircleIcon />
                              </Avatar>
                              <div className="user-reports-grid-user-details">
                                <Typography variant="body2" className="user-reports-grid-username">
                                  {report.UserId?.username || "N/A"}
                                </Typography>
                                <Typography variant="caption" className="user-reports-grid-mobile">
                                  {report.UserId?.mobile || "N/A"}
                                </Typography>
                              </div>
                            </div>
                          </div>
                          
                          <div className="user-reports-grid-user-section">
                            <div className="user-reports-grid-user-label">
                              <PersonIcon style={{ fontSize: 16, marginRight: 4 }} />
                              Reported User
                            </div>
                            <div className="user-reports-grid-user-info">
                              <Avatar
                                src={getProfilePicUrl(report.ReportId?.ProfilePic)}
                                alt={report.ReportId?.username}
                                className="user-reports-grid-avatar"
                              >
                                <AccountCircleIcon />
                              </Avatar>
                              <div className="user-reports-grid-user-details">
                                <Typography variant="body2" className="user-reports-grid-username">
                                  {report.ReportId?.username || "N/A"}
                                </Typography>
                                <Typography variant="caption" className="user-reports-grid-mobile">
                                  {report.ReportId?.mobile || "N/A"}
                                </Typography>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="user-reports-grid-reason">
                          <Typography variant="caption" className="user-reports-grid-reason-label">
                            Reason:
                          </Typography>
                          <Typography variant="body2" className="user-reports-grid-reason-text">
                            {report.Reason || "No reason provided"}
                          </Typography>
                        </div>
                      </CardContent>
                      <CardActions className="user-reports-grid-actions">
                        {isMobile && (
                          <Typography variant="caption" className="user-reports-grid-mobile-date">
                            {moment(report.Date).utcOffset('+06:00').format("DD MMM YYYY")}
                          </Typography>
                        )}
                        <Button
                          className="report-delete-button"
                          onClick={() => handleDeleteReport(report._id)}
                          size="small"
                          startIcon={<DeleteIcon />}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* List/Table View */}
          {viewMode === 'list' && (
          <TableContainer component={Paper} className="user-reports-table-container">
            {loading && <div className="google-progress-bar"></div>}
            <Table className="user-reports-table" aria-label="user reports table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      indeterminate={selectedReports.length > 0 && selectedReports.length < reports.length}
                    />
                  </TableCell>
                  <TableCell align="left">Reporter</TableCell>
                  {!isSmallMobile && <TableCell align="left">Reported User</TableCell>}
                  <TableCell align="left">Reason</TableCell>
                  {!isMobile && <TableCell align="center">Date</TableCell>}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.length <= 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? (isSmallMobile ? 4 : 5) : 6} align="center">
                      <div className="user-reports-no-data-available">No user reports available</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  reports.map((report) => (
                    <TableRow key={report._id} className="user-reports-table-row">
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedReports.some(r => r._id === report._id)}
                          onChange={() => handleSelectReport(report)}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <div className="user-info">
                          <Avatar
                            src={getProfilePicUrl(report.UserId?.ProfilePic)}
                            alt={report.UserId?.username}
                            className="user-avatar"
                          />
                          <div className="user-details">
                            <span className="username">{report.UserId?.username || "N/A"}</span>
                            <span className="mobile">{report.UserId?.mobile || "N/A"}</span>
                          </div>
                        </div>
                      </TableCell>
                      {!isSmallMobile && (
                        <TableCell align="left">
                          <div className="user-info">
                            <Avatar
                              src={getProfilePicUrl(report.ReportId?.ProfilePic)}
                              alt={report.ReportId?.username}
                              className="user-avatar"
                            />
                            <div className="user-details">
                              <span className="username">{report.ReportId?.username || "N/A"}</span>
                              <span className="mobile">{report.ReportId?.mobile || "N/A"}</span>
                            </div>
                          </div>
                        </TableCell>
                      )}
                      <TableCell align="left">
                        <div className="report-reason">{report.Reason || "No reason provided"}</div>
                      </TableCell>
                      {!isMobile && (
                        <TableCell align="center">
                          <span className="report-date">
                            {moment(report.Date).utcOffset('+06:00').format("DD MMM YYYY, hh:mm A")}
                          </span>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Button
                          className="report-delete-button"
                          onClick={() => handleDeleteReport(report._id)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          
          {/* Pagination - shown for both views */}
          <div className="user-reports-pagination-container">
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              size="medium"
              siblingCount={1}
              boundaryCount={1}
              shape="rounded"
              className="user-reports-pagination"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserReports;
